import moment from 'moment';
import { ElMessage } from 'element-plus';
import copy from 'copy-to-clipboard';
import { RouteLocationNormalized } from 'vue-router';
import * as Sentry from '@sentry/vue';

import { PAYMENT_CHANNELS, LOGIN_REQUIRED_ROUTES } from '@/constants';
import { IPaymentMethod } from '@/types/invoice';

export const getDayAgoTs = (numberOfDay: number) => {
	return moment().subtract(numberOfDay, 'days').valueOf();
};

export const getMonthAgoTs = (numberOfMonth: number) => {
	return moment().subtract(numberOfMonth, 'months').valueOf();
};

export const getTimeAgoTs = (input: string) => {
	const _value = input.split('--')[0];
	const number = parseInt(_value);
	const type = input.split('--')[1];
	if (type === 'days') {
		return getDayAgoTs(number);
	} else if (type === 'months') {
		return getMonthAgoTs(number);
	}
	return moment().valueOf();
};

export const getPaymentMethod = (pm: IPaymentMethod) => {
	const pmMethodConfig = useGet(PAYMENT_CHANNELS, pm.pmMethodId + '', { id: '', logo: '' });
	return {
		...pm,
		...pmMethodConfig,
	};
};

export const getPaymentMethodLogo = (pmMethodId: number) => {
	return useGet(PAYMENT_CHANNELS, pmMethodId + '', { logo: '' }).logo;
};

export const getUriWithParam = (baseUrl: string, params: Record<string, any>): string => {
	const Url = new URL(baseUrl);
	const urlParams: URLSearchParams = new URLSearchParams(Url.search);
	for (const key in params) {
		if (params[key] !== undefined) {
			urlParams.set(key, params[key]);
		}
	}
	Url.search = urlParams.toString();
	return Url.toString();
};

export const uid = () => {
	return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const handleCopy = async (data: string) => {
	const { $i18n } = useNuxtApp();
	try {
		copy(data, {
			format: 'text/plain',
		});
		ElMessage({
			message: `${$i18n.t('copied')} ${data}`,
			type: 'success',
		});
	} catch (error) {
		console.log({ error });
		Sentry.captureException(error);
	}
};

export const convertToUnixTimestamp = (dateString: string) => {
	const date = new Date(dateString);
	const unixTimestamp = String(Math.floor(date.getTime())); // Convert milliseconds to seconds

	return unixTimestamp;
};

export const convertToDateString = (unixMilliseconds: number) => {
	const date = new Date(unixMilliseconds);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const convertToTimeString = (unixMilliseconds: number) => {
	const date = new Date(unixMilliseconds);
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');

	return `${hours}:${minutes}:${seconds}`;
};

export const detectOrderIdOrCardName = (inputString: string) => {
	const orderIdRegex = /\b\d{19}\b/;
	const orderIdMatch = inputString.match(orderIdRegex);

	if (orderIdMatch) {
		return 'orderId';
	} else {
		return 'cardName';
	}
};

export const setWithExpiry = (key: string, value: number, ttl: number) => {
	const now = new Date();

	const item = {
		value: value,
		expiry: now.getTime() + ttl,
	};
	localStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = (key: string) => {
	const itemStr = localStorage.getItem(key);

	if (!itemStr) {
		return null;
	}
	const item = JSON.parse(itemStr);
	const now = new Date();

	if (now.getTime() > item.expiry) {
		localStorage.removeItem(key);
		return null;
	}
	return item.value;
};

export const isValidEmail = (email: string) => {
	const emailRegex =
		/^(([^<>^!#$%&'*+/=(){|}`~\[\]\\.,;:\s@"]+(\.[^<>^!#$%&'*+/=(){|}`~\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9]+[-.])*[a-zA-Z0-9]+([-.][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}))$/;

	return emailRegex.test(email);
};

export const openNewTab = (urlRedirect: string) => {
	// Create link in memory
	const a = window.document.createElement('a');

	a.target = '_blank';
	a.href = urlRedirect;

	// Dispatch fake click
	const e = window.document.createEvent('MouseEvents');

	e.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
	a.dispatchEvent(e);
};

export function isRequiredLoginRoute(routeName: RouteLocationNormalized['name']): boolean {
	if (typeof routeName !== 'string') {
		return false;
	}
	return LOGIN_REQUIRED_ROUTES.some((r) => routeName.includes(r));
}
